/**
 * Shared hook for approve/reject/queue actions on agent decisions.
 * Used by both PendingApprovalsCard (dashboard) and Approvals page.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { agentService } from '@/services/agent';
import { useAgentStore } from '@/stores/agentStore';
import { toast } from '@/hooks/useToast';

export function useApprovalActions() {
  const removePendingApproval = useAgentStore((s) => s.removePendingApproval);
  const queryClient = useQueryClient();
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['agent', 'pending-approvals'] });
    queryClient.invalidateQueries({ queryKey: ['agent', 'decisions'] });
  };

  const withProcessing = async (id: string, fn: () => Promise<void>) => {
    setProcessingIds((prev) => new Set(prev).add(id));
    try {
      await fn();
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleApprove = async (id: string) => {
    await withProcessing(id, async () => {
      try {
        await agentService.approveDecision(id);
        removePendingApproval(id);
        invalidate();
        toast({ title: 'Decision Approved', description: 'Order will be executed', variant: 'success' });
      } catch {
        toast({ title: 'Action Failed', description: 'Could not approve decision', variant: 'destructive' });
      }
    });
  };

  const handleReject = async (id: string) => {
    await withProcessing(id, async () => {
      try {
        await agentService.rejectDecision(id);
        removePendingApproval(id);
        invalidate();
        toast({ title: 'Decision Rejected', variant: 'default' });
      } catch {
        toast({ title: 'Action Failed', description: 'Could not reject decision', variant: 'destructive' });
      }
    });
  };

  const handleQueue = async (id: string) => {
    await withProcessing(id, async () => {
      try {
        await agentService.queueDecision(id);
        removePendingApproval(id);
        invalidate();
        toast({ title: 'Decision Queued', description: 'Will execute at market open', variant: 'default' });
      } catch {
        toast({ title: 'Action Failed', description: 'Could not queue decision', variant: 'destructive' });
      }
    });
  };

  const isProcessing = (id: string) => processingIds.has(id);

  return { handleApprove, handleReject, handleQueue, isProcessing };
}
